import { Checkbox, CheckboxGroup, Choicebox, FormGroup, RadioGroup } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { Fragment } from 'react/jsx-runtime';
import { supplyCategoriesByEpisodeCategory } from '@common/constants/supplyCategoriesByEpisodeCategory';
import { getSelectedLanguage } from '@frontend/translate';
import { useFormContext } from '@frontend/utils/hooks/useFormContext';
import type { OrderSuppliesFormState } from '../../types/orderSuppliesFormState';
import type { OrderSuppliesRouteParams } from '../../types/orderSuppliesRouteParams';
import { routeParamToEpisodeCategory, routeParamToSupplyCategory } from '../../utils/orderSuppliesRouteUtils';

export function OrderSuppliesOrderProduct() {
    const translate = getSelectedLanguage();
    const { episodeCategory: episodeCategoryParam, supplyCategory: supplyCategoryParam } =
        useParams() as OrderSuppliesRouteParams;
    const form = useFormContext<OrderSuppliesFormState>();

    const episodeCategory = episodeCategoryParam ? routeParamToEpisodeCategory(episodeCategoryParam) : undefined;
    const supplyCategory = supplyCategoryParam ? routeParamToSupplyCategory(supplyCategoryParam) : undefined;

    if (!episodeCategory || !supplyCategory) {
        return null;
    }

    const products = supplyCategoriesByEpisodeCategory[episodeCategory]?.[supplyCategory]?.products || [];
    const selectionMode = supplyCategoriesByEpisodeCategory[episodeCategory]?.[supplyCategory]?.selectionMode;

    if (selectionMode === 'multiple') {
        return (
            <FormGroup
                isRequired={true}
                label={translate.orderSupplies.selectProducts}
            >
                <CheckboxGroup
                    error={form.errors.getMessage('order.selectedProducts')}
                    onChange={(selectedValues) => {
                        form.handleChange({
                            name: 'order.selectedProducts',
                            value: selectedValues,
                        });
                    }}
                    value={form.values.order.selectedProducts ?? []}
                >
                    {products.map((product) => (
                        <Checkbox
                            disabled={form.isSubmitting}
                            key={product.value}
                            label={product.label}
                            value={product.value}
                        />
                    ))}
                </CheckboxGroup>
            </FormGroup>
        );
    }

    if (selectionMode === 'single') {
        return (
            <FormGroup
                isRequired={true}
                label={translate.orderSupplies.selectProduct}
            >
                <RadioGroup
                    error={form.errors.getMessage('order.selectedProduct')}
                    layout="row"
                    name="order.selectedProduct"
                    onChange={form.handleChange}
                    value={form.values.order.selectedProduct ?? ''}
                >
                    {products.map((product) => (
                        <Choicebox
                            disabled={form.isSubmitting}
                            key={product.value}
                            label={
                                <span style={{ display: 'block', maxWidth: '160px' }}>
                                    {product.image && (
                                        <img
                                            alt={product.label}
                                            src={product.image}
                                            style={{ marginBottom: '4px' }}
                                            width="160px"
                                        />
                                    )}
                                    {product.label}
                                    {product.description &&
                                        product.description.map((text, index) => (
                                            <Fragment key={`${product.value}-desc-${index}-${text}`}>
                                                <br />
                                                {text}
                                            </Fragment>
                                        ))}
                                </span>
                            }
                            value={product.value}
                        />
                    ))}
                </RadioGroup>
            </FormGroup>
        );
    }

    return null;
}
